<template>
  <main-container title="データ変換">
    <div slot="main-content">
      <form-container class="data-convert-container">
        <form-item-container label="変換方法選択">
          <field-container>
            <div class="form-standard converter-select" @click="visibleConverterSelect = true">
              {{selectedConverter ? selectedConverter.name : '変換方法を選択する'}}
            </div>
            <converter-select v-if="visibleConverterSelect"
                              :convertersCategories="converters"
                              :selected-converter-for-init="selectedConverter"
                              @close="visibleConverterSelect = false"
                              @select="onSelectConverter">
            </converter-select>
          </field-container>
        </form-item-container>

        <template v-if="selectedConverter">
          <form-item-container :label="putUrl === putRsUrl ? '画像ファイル選択' : '変換データ選択'"
                               :tip="isRsConvertMethod() ? rsLabelTip: dsLabelTip">
            <div slot="form-item-annotation">
              <div class="annotation">* 500MBまで</div>
            </div>
            <field-container :class="{ 'has-error': dsValidateError }">
              <field-upload-ds :ds="ds"
                               :url="putUrl"
                               :method="'convert'"
                               :multiple="false"
                               @change="val => this.ds = val"
                               @before-add="onBeforeAdd"
                               @added="onUploaded"
                               @removed="onRemoved"
                               style="width: 100%;"></field-upload-ds>
              <span class="error-message"><strong>変換データを選択してください</strong></span>
            </field-container>
          </form-item-container>
          <form-item-container label="変換データの削除" :annotation="'* データ変換に使用したデータをサーバ上から削除します'">
            <field-container>
              <field-radio-group v-model="deleteUploadFile" name="deleteUploadFile"
                                 :options="[{label: '削除しない', value: false}, {label: '削除する', value: true}]"
                                 @change="onChangeDeleteDsOption"></field-radio-group>
            </field-container>
          </form-item-container>

          <form-item-container v-if="hyperParams && hyperParams.length > 0" label="オプション設定"
                               annotation="* 不明な場合は値を変更しなくて構いません">
            <hyper-params :key="selectedConverter.id" :hyperParams="hyperParams"
                          :hyperParamSummaries="hyperParamSummaries"></hyper-params>
          </form-item-container>
        </template>
      </form-container>

      <convert-result-dialog v-if="convertResult != null && visibleConvertResult"
                             :visible="visibleConvertResult"
                             :converterName="selectedConverter.name"
                             :uploadFileName="uploadFileName"
                             :convertResult="convertResult"
                             @close="visibleConvertResult=false">
      </convert-result-dialog>
    </div>
    <template slot="main-footer">
      <el-button class="large" type="primary" @click="onClickConvertResultList">変換結果一覧</el-button>
      <el-button class="large" type="info" @click="visibleConvertResult=true" v-show="convertResult != null">結果を表示
      </el-button>
      <el-button :disabled="!(selectedConverter && ds.dsName)" class="large" type="primary" @click="onClickConvert">
        データを変換
      </el-button>
    </template>
  </main-container>
</template>

<script lang="js">
import CommonUtil from '../../utils/common'
import BaseView from '../base'
import HyperParamsCmp from '../../components/form/field/hyper-params.vue'
import ConverterSelectCmp from '../../components/data-convert/dialogs/converter-select.vue'
import ConvertResultDialogCmp from '../../components/data-convert/dialogs/convert-result.vue'
import { ConverterHyperParams, ConverterHyperParamTip } from '../../defines/convert'
import WebApiClient from '../../api/web'
import { Code } from '../../api/base'

const DS_LABEL_TIP = `・変換対象となるデータを選択してください。
                       ・ファイル形式は、CSVファイルにのみ対応しています。
                       ・"key"または"id"という項目名のカラムを用意すると、データを識別する項目として選択されます。
                       なければ、各データに連番を付与します。`

const RS_LABEL_TIP = `・全ての画像をZIP形式で圧縮したデータを選択してください。`

export default {
  name: 'ConvertView',
  mixins: [BaseView],
  components: {
    'hyper-params': HyperParamsCmp,
    'converter-select': ConverterSelectCmp,
    'convert-result-dialog': ConvertResultDialogCmp
  },
  props: {},
  computed: {
    dsLabelTip () {
      return DS_LABEL_TIP
    },
    rsLabelTip () {
      return RS_LABEL_TIP
    },
    putDsUrl () {
      return `${this.$session.get('API_URL')}ds/put`
    },
    putRsUrl () {
      return `${this.$session.get('API_URL')}rs/put`
    }
  },
  data () {
    return {
      putUrl: null,
      ds: {
        dsName: null,
        rsName: null,
        uploadFiles: []
      },

      uploadFileName: '',

      dsValidateError: false,
      deleteUploadFile: false,

      uploading: false,

      converters: [],

      selectedConverter: null,

      hyperParams: [],
      hyperParamSummaries: [],

      visibleConverterSelect: false,

      convertResult: null,
      visibleConvertResult: false
    }
  },
  created () {
    WebApiClient.getConverts(this, null).then(response => {
      this.converters = response.data.result.converters
    })
  },
  methods: {
    isRsConvertMethod () {
      return this.putUrl === this.putRsUrl
    },

    onUploaded (ds) {
      this.uploadFileName = ds.uploadFiles.slice(-1)[0].originalFileName
      this.dsValidateError = false
    },

    onRemoved () {
      this.dsValidateError = true
    },

    onSelectConverter (converter) {
      const converterId = converter.id
      if (CommonUtil.isNotNull(this.selectedConverter) && this.selectedConverter.id === converterId) {
        return
      }
      this.putUrl = converter.type === 'IMG' ? this.putRsUrl : this.putDsUrl

      const thisConverterHyperParams = ConverterHyperParams[converterId] || []
      let hyperParamSummaries = []
      thisConverterHyperParams.forEach((hyperParam) => {
        const tip = CommonUtil.isNotEmpty(hyperParam.tipId)
          ? ConverterHyperParamTip[hyperParam.tipId]
          : ConverterHyperParamTip[hyperParam.hyperParamId]

        hyperParamSummaries.push({
          'hyperParamId': hyperParam.hyperParamId,
          'hyperParamName': hyperParam.hyperParamName,
          'defaultVal': hyperParam.defaultVal,
          'description': { 'text': tip }
        })
      })

      this.selectedConverter = converter
      let hyperParams = []
      for (let hyperParamSummary of hyperParamSummaries) {
        hyperParams.push({
          hyperParamId: hyperParamSummary.hyperParamId,
          value: hyperParamSummary.defaultVal
        })
      }

      this.hyperParamSummaries = hyperParamSummaries
      this.hyperParams = hyperParams
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    onClickConvert () {
      this.isValid().then(valid => {
        if (!valid) return

        const param = {
          converterId: this.selectedConverter.id,
          dsName: this.ds.dsName,
          rsName: this.ds.rsName,
          deleteUploadFile: this.deleteUploadFile,
          option: this.buildConvertOption()
        }
        WebApiClient.postConverts(this, param).then(response => {
          if (response.data.code === Code.SUCCESS) {
            this.convertResult = response.data.result
            this.visibleConvertResult = true
            this.$forceUpdate()
          } else if (response.data.code === Code.TIMEOUT) {
            this.changeLocation('data-convert-result-list', null, false, true)
          }
        }).catch(() => {})
      })
    },
    buildConvertOption () {
      let options = []
      this.hyperParams.forEach(hyperParam => {
        const hyperParamId = hyperParam.hyperParamId
        let value = hyperParam.value

        if (hyperParamId === 'stop_words') {
          value = CommonUtil.isNotEmpty(value) ? String(value).split(',') : null
        }
        options.push({
          hyperParamId: hyperParamId,
          value: value
        })
      })
      return options
    },

    onClickConvertResultList () {
      this.changeLocation('data-convert-result-list', null, false, true)
    }
  }
}

</script>

<style scoped>
  .data-convert-container {
    max-width: 1000px;
    padding: 24px;
    height: 100%;
  }

  .converter-select {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration-line: underline;
    color: #2456d4;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/views/data/convert.vue